<template>
  <div class="main-layout">
    <el-container>
      <!-- 侧边栏 -->
      <el-aside width="240px" class="sidebar">
        <div class="logo">
          <h2>考试练习平台</h2>
          <p>管理后台</p>
        </div>
        <el-menu
          :default-active="activeMenu"
          router
          class="sidebar-menu"
        >
          <el-menu-item index="/dashboard">
            <el-icon><DataLine /></el-icon>
            <span>数据概览</span>
          </el-menu-item>
          
          <el-menu-item index="/banner">
            <el-icon><Picture /></el-icon>
            <span>轮播图管理</span>
          </el-menu-item>
          
          <el-sub-menu index="content">
            <template #title>
              <el-icon><Document /></el-icon>
              <span>内容管理</span>
            </template>
            <el-menu-item index="/region">地区管理</el-menu-item>
            <el-menu-item index="/paper">试卷管理</el-menu-item>
            <el-menu-item index="/single-question">单一题管理</el-menu-item>
            <el-menu-item index="/standard-word">规范词管理</el-menu-item>
            <el-menu-item index="/my-page-option">选项管理</el-menu-item>
            <el-menu-item index="/prompt-config">提示词管理</el-menu-item>
          </el-sub-menu>
          
          <el-sub-menu index="user">
            <template #title>
              <el-icon><User /></el-icon>
              <span>用户管理</span>
            </template>
            <el-menu-item index="/user">普通用户</el-menu-item>
            <el-menu-item index="/admin">管理员</el-menu-item>
          </el-sub-menu>
          
          <el-sub-menu index="marketing">
            <template #title>
              <el-icon><Promotion /></el-icon>
              <span>营销管理</span>
            </template>
            <el-menu-item index="/vip-package">会员套餐</el-menu-item>
            <el-menu-item index="/coupon">优惠券管理</el-menu-item>
            <el-menu-item index="/invitation">邀请记录</el-menu-item>
            <el-menu-item index="/consumption">消费记录</el-menu-item>
          </el-sub-menu>
          
          <el-menu-item index="/profile">
            <el-icon><UserFilled /></el-icon>
            <span>个人中心</span>
          </el-menu-item>
        </el-menu>
      </el-aside>
      
      <!-- 主内容区 -->
      <el-container>
        <el-header class="header">
          <div class="header-left">
            <h3>{{ pageTitle }}</h3>
          </div>
          <div class="header-right">
            <el-dropdown @command="handleCommand">
              <span class="admin-info">
                <el-icon><Avatar /></el-icon>
                <span>管理员</span>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="logout">退出登录</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </el-header>
        
        <el-main class="main-content">
          <router-view />
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  Menu, 
  Fold, 
  Expand, 
  HomeFilled, 
  Picture, 
  Document, 
  User, 
  UserFilled,
  Promotion, 
  Wallet 
} from '@element-plus/icons-vue'

const router = useRouter()
const route = useRoute()

const activeMenu = computed(() => route.path)

const pageTitleMap: Record<string, string> = {
  '/dashboard': '数据概览',
  '/banner': '轮播图管理',
  '/region': '地区管理',
  '/paper': '试卷管理',
  '/single-question': '单一题管理',
  '/standard-word': '规范词管理',
  '/user': '普通用户管理',
  '/admin': '管理员管理',
  '/vip-package': '会员套餐管理',
  '/coupon': '优惠券管理',
  '/invitation': '推广记录管理',
  '/consumption': '会员开通记录',
  '/profile': '个人中心',
  '/prompt-config': '提示词管理',
  '/my-page-option': '选项管理'
}

const pageTitle = computed(() => {
  // 处理动态路由的页面标题
  if (route.path.includes('/paper/') && route.path.includes('/questions')) {
    return '题目管理'
  }
  return pageTitleMap[route.path] || '管理后台'
})

const handleCommand = (command: string) => {
  if (command === 'logout') {
    ElMessageBox.confirm('确定要退出登录吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      localStorage.removeItem('admin_token')
      router.push('/login')
      ElMessage.success('已退出登录')
    })
  }
}
</script>

<style scoped>
.main-layout {
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  overflow: hidden;
}

.el-container {
  height: 100%;
}

/* 侧边栏样式 */
.sidebar {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  overflow-y: auto;
}

.logo {
  padding: 30px 20px;
  text-align: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.logo h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  background: linear-gradient(135deg, #a0826d 0%, #8b7355 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.logo p {
  margin: 8px 0 0;
  font-size: 13px;
  color: #909399;
}

.sidebar-menu {
  border: none;
  background: transparent;
  padding: 10px;
}

:deep(.el-menu-item),
:deep(.el-sub-menu__title) {
  border-radius: 8px;
  margin-bottom: 4px;
  transition: all 0.3s;
}

:deep(.el-menu-item:hover),
:deep(.el-sub-menu__title:hover) {
  background: rgba(201, 184, 163, 0.15) !important;
}

:deep(.el-menu-item.is-active) {
  background: linear-gradient(135deg, #c9b8a3 0%, #a89985 100%) !important;
  color: white !important;
  box-shadow: 0 4px 12px rgba(160, 130, 109, 0.3);
}

/* 头部样式 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 0 30px;
}

.header-left h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.admin-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 20px;
  transition: all 0.3s;
}

.admin-info:hover {
  background: rgba(201, 184, 163, 0.15);
}

/* 主内容区样式 */
.main-content {
  padding: 24px;
  overflow-y: auto;
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.02);
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}
</style>
